import React, { useState, useEffect, useRef } from 'react';

// Definição das máscaras disponíveis
const MASKS = {
  cpf: { pattern: '999.999.999-99', placeholder: '___.___.___-__' },
  telefone: { pattern: '(99) 99999-9999', placeholder: '(__) _____-____' },
  cep: { pattern: '99999-999', placeholder: '_____-___' },
  data: { pattern: '99/99/9999', placeholder: '__/__/____' }
};

// Função para aplicar máscara
const applyMask = (value, pattern) => {
  if (!value || !pattern) return value;
  
  // Remove todos os caracteres não numéricos
  const digits = value.replace(/\D/g, '');
  
  let masked = '';
  let digitIndex = 0;
  
  for (let i = 0; i < pattern.length && digitIndex < digits.length; i++) {
    if (pattern[i] === '9') {
      masked += digits[digitIndex];
      digitIndex++;
    } else {
      masked += pattern[i];
    }
  }
  
  return masked;
};

// Função para remover máscara
const removeMask = (value) => {
  return value ? value.replace(/\D/g, '') : '';
};

const CustomMaskedInput = ({
  mask,
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder,
  className = '',
  disabled = false,
  autoFocus = false,
  id,
  name,
  type = 'text',
  ...otherProps
}) => {
  const inputRef = useRef(null);
  const [internalValue, setInternalValue] = useState('');

  // Configuração da máscara
  const maskConfig = MASKS[mask] || { pattern: mask, placeholder: placeholder };

  // Sincronizar valor externo
  useEffect(() => {
    if (value !== undefined) {
      const stringValue = value ? value.toString() : '';
      const masked = applyMask(stringValue, maskConfig.pattern);
      setInternalValue(masked);
    }
  }, [value, maskConfig.pattern]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    // Aplicar máscara ao valor
    const maskedValue = applyMask(inputValue, maskConfig.pattern);
    setInternalValue(maskedValue);

    // Chamar onChange com valor sem máscara
    if (onChange) {
      const unmaskedValue = removeMask(maskedValue);
      onChange({
        target: {
          ...e.target,
          name: name || e.target.name,
          value: maskedValue // Enviamos o valor mascarado para manter consistência
        }
      });
    }

    // Ajustar cursor após a renderização
    setTimeout(() => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        let newPosition = cursorPosition;
        
        // Se adicionamos caracteres da máscara, ajustar posição
        if (maskedValue.length > inputValue.length) {
          newPosition = Math.min(cursorPosition + (maskedValue.length - inputValue.length), maskedValue.length);
        }
        
        inputRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    const { key, ctrlKey, metaKey } = e;
    
    // Permitir teclas de controle
    if (ctrlKey || metaKey || 
        ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
         'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) {
      return;
    }
    
    // Para máscaras numéricas, permitir apenas dígitos
    if (maskConfig.pattern.includes('9') && !/\d/.test(key)) {
      e.preventDefault();
    }
  };

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur({
        target: {
          ...e.target,
          name: name || e.target.name,
          value: internalValue // Mantém o valor mascarado
        }
      });
    }
  };

  const handleFocus = (e) => {
    if (onFocus) {
      onFocus(e);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const cleanText = removeMask(pastedText);
    const maskedValue = applyMask(cleanText, maskConfig.pattern);
    
    setInternalValue(maskedValue);
    
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: maskedValue
        }
      });
    }
  };

  return (
    <input
      ref={inputRef}
      type={type}
      id={id}
      name={name}
      value={internalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      placeholder={placeholder || maskConfig.placeholder}
      className={className}
      disabled={disabled}
      autoFocus={autoFocus}
      {...otherProps}
    />
  );
};

export default CustomMaskedInput;

// Hook personalizado para usar máscaras (mantido para compatibilidade)
export const useMask = (initialValue = '', maskType) => {
  const [value, setValue] = useState(initialValue);
  const [maskedValue, setMaskedValue] = useState('');

  const maskConfig = MASKS[maskType];

  useEffect(() => {
    if (maskConfig) {
      const masked = applyMask(value, maskConfig.pattern);
      setMaskedValue(masked);
    }
  }, [value, maskConfig]);

  const handleChange = (inputValue) => {
    const unmasked = removeMask(inputValue);
    setValue(unmasked);
  };

  return {
    value: maskedValue,
    rawValue: value,
    onChange: handleChange,
    setValue
  };
}; 